import { type StaticDecode, Type } from 'typebox';
import {
  LocString,
  Nullable,
  ReadonlyArray,
  ReadonlyInterface,
  ReadonlyObject,
} from './schema';
import { Image, MongoAndPgEntity } from './shared';

export const Role = Type.Union([
  Type.Literal('external'),
  Type.Literal('limited_access'),
  Type.Literal('consultant'),
  Type.Literal('countrymanager'),
  Type.Literal('referencemanager'),
  Type.Literal('internationalmanager'),
  Type.Literal('departmentmanager'),
  // the below are for API users
  Type.Literal('proposal'),
  Type.Literal('readonly_consultant'),
  Type.Literal('readonly_countrymanager'),
  Type.Literal('readonly_internationalmanager'),
  Type.Literal('readonly_proposal'),
]);
export type Role = StaticDecode<typeof Role>;

const BaseUser = ReadonlyObject({
  company_id: Type.String(),
  office_id: Nullable(Type.String()),
  country_id: Nullable(Type.String()),
  country_code: Nullable(Type.String()),
  role: Role,
  roles: ReadonlyArray(Role),
  created_at: Type.String(),
});

export const User = ReadonlyInterface([MongoAndPgEntity, BaseUser], {
  user_id: Type.String(),
  name: Type.String(),
  external_unique_id: Nullable(Type.String()),
  company_name: Type.String(),
  email: Type.String(),
  telephone: Nullable(Type.String()),
  language_code: Type.String(),
  language_codes: ReadonlyArray(Type.String()),
  ui_language_code: Type.String(),
  company_group_ids: ReadonlyArray(Type.String()),
  masterdata_languages: ReadonlyArray(Type.String()),
  override_language_code: Nullable(Type.String()),
  default_cv_id: Nullable(Type.String()),
  default_cv_template_id: Nullable(Type.String()),
  office_name: Nullable(Type.String()),
  company_subdomains: ReadonlyArray(Type.String()),
  role_allowed_office_ids: Nullable(ReadonlyArray(Type.String())),
  role_allowed_tag_ids: Nullable(ReadonlyArray(Type.String())),
  selected_tag_ids: ReadonlyArray(Type.String()),
  selected_office_ids: ReadonlyArray(Type.String()),
  international_toggle: Type.Union([
    Type.Literal('expand'),
    Type.Literal('hide'),
  ]),
  include_officeless_reference_projects: Type.Boolean(),
  expand_proposals_toggle: Type.Boolean(),
  preferred_download_format: Type.String(),
  deactivated: Type.Boolean(),
  deactivated_at:
    // `false` if not deactivated, otherwise timestamp
    Type.Union([Type.Literal(false), Type.String()]),
  image: Image,
  is_authenticated_by_password: Type.Boolean(),
  upn: Nullable(Type.String()),
  title: LocString,
  updated_at: Type.String(),
});
export type User = StaticDecode<typeof User>;

export const ApiUser = ReadonlyInterface([MongoAndPgEntity, BaseUser], {
  name: Nullable(Type.String()),
  technical_contact: Nullable(Type.String()),
  access_token: Type.String(),
  enabled: Type.Boolean(),
  created_by: Nullable(Type.String()),
});
export type ApiUser = StaticDecode<typeof ApiUser>;

export type UserOrApiUser = User | ApiUser;

export function isApiUser(user: UserOrApiUser): user is ApiUser {
  return typeof (user as ApiUser).access_token === 'string';
}
